import React, { useEffect, useRef } from 'react';
import { makeStyles, Button } from '@material-ui/core';
import { Error as ErrorIcon } from '@material-ui/icons';
import { FormControl, InputLabel, Select } from '@mui/material';
import { styled } from '@mui/material/styles';

import { Typography } from '@redshelf/redshelf-ui';
import { PrintMenuHeader } from './';
import { isNumber } from 'lodash';
import { PAGE_SELECTION_ERRORS } from 'components/PrintMenu/PrintMenu';

const useStyles = makeStyles((theme) => {
  return {
    pageRangeForm: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      justifyContent: 'space-around',
    },
    requiredMessage: {
      fontSize: theme.spacing(1.5),
      paddingTop: theme.spacing(3),
    },
    formBody: {
      display: 'flex',
      marginTop: theme.spacing(2),
      alignItems: 'flex-start',
      justifyContent: 'space-around',
      width: '100%',
    },
    pageRangeSelectGroup: {
      display: 'flex',
      flex: 1,
      justifyContent: 'space-around',
      margin: 'auto',
    },
    selectDivider: {
      padding: theme.spacing(1),
    },
    addButton: ({ displayAddButtonError }) => ({
      marginLeft: theme.spacing(2),
      height: theme.spacing(5.5),
      width: theme.spacing(9),
      outline: displayAddButtonError ? '2px solid #D32F2F' : '',
      outlineOffset: displayAddButtonError ? 2 : '',
    }),
    errorContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      margin: theme.spacing(0.5, 1),
    },
    errorIcon: {
      color: '#D32F2F',
      fontSize: theme.spacing(2),
    },
    errorMessage: {
      fontSize: theme.spacing(1.5),
      color: '#D32F2F',
    },
  };
});

const StyledFormControl = styled(FormControl)(() => ({
  width: '45%',
}));

const StyledInputLabel = styled(InputLabel)(() => ({
  '&.MuiInputLabel-shrink': {
    transform: 'translate(14px, -9px) scale(0.75)',
  },

  transform: 'translate(14px, 12px) scale(1)',
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  '& .MuiNativeSelect-select': {
    height: theme.spacing(5.5),
    paddingTop: 0,
    paddingBottom: 0,
    margin: 0,
    textOverflow: 'ellipsis',
  },
}));

const ErrorMessage = ({ error }) => {
  const classes = useStyles();

  if (!error) {
    return <span id="error-message"></span>;
  }

  return (
    <p className={classes.errorContainer} id="error-message">
      <ErrorIcon className={classes.errorIcon} />
      <Typography className={classes.errorMessage} component="span">
        {error.message}
      </Typography>
    </p>
  );
};

const START_PAGE_LABEL = 'Start Page*';
const END_PAGE_LABEL = 'End Page*';

const PageSelection = ({
  pageRangeEnd,
  pageRangeStart,
  pages,
  onSetPageRangeStart,
  onSetPageRangeEnd,
  onAddPageRangeClick = () => {},
  error,
}) => {
  const addButtonRef = useRef();
  const startPageSelectRef = useRef();
  const endPageSelectRef = useRef();
  const displayAddButtonError =
    error?.errorFocus === PAGE_SELECTION_ERRORS.ADD_BUTTON;
  const classes = useStyles({
    displayAddButtonError,
  });

  useEffect(
    function focusOnError() {
      if (!error) {
        return;
      }

      switch (error.errorFocus) {
        case PAGE_SELECTION_ERRORS.ADD_BUTTON:
          addButtonRef.current.focus();
          break;
        case PAGE_SELECTION_ERRORS.START_PAGE:
          startPageSelectRef.current.focus();
          break;
        case PAGE_SELECTION_ERRORS.END_PAGE:
          endPageSelectRef.current.focus();
          break;
        default:
          return;
      }
    },
    [error],
  );

  return (
    <form className={classes.pageRangeForm} onSubmit={onAddPageRangeClick}>
      <PrintMenuHeader headerText="Choose Pages to Print to a PDF" />
      <Typography className={classes.requiredMessage} component="span">
        *Required Fields
      </Typography>
      <div className={classes.formBody}>
        <div
          aria-labelledby="page-range-select-group"
          role="group"
          className={classes.pageRangeSelectGroup}
        >
          <StyledFormControl
            error={error?.errorFocus === PAGE_SELECTION_ERRORS.START_PAGE}
            variant="outlined"
            size="small"
          >
            <StyledInputLabel htmlFor="start-page-select">
              {START_PAGE_LABEL}
            </StyledInputLabel>
            <StyledSelect
              inputProps={{
                ...(Boolean(
                  error?.errorFocus === PAGE_SELECTION_ERRORS.START_PAGE,
                )
                  ? { 'aria-describedby': 'error-message' }
                  : ''),
                id: 'start-page-select',
                ref: startPageSelectRef,
                'aria-required': true,
              }}
              label={START_PAGE_LABEL}
              native
              onChange={(e) => {
                const value = !isNaN(e.target.value)
                  ? +e.target.value
                  : e.target.value;

                onSetPageRangeStart(value);
              }}
              value={pageRangeStart}
              variant="outlined"
            >
              <option disabled value=""></option>
              {pages.map((page) => (
                <option
                  className="noTranslate"
                  key={`start-page-${page.index}`}
                  value={page.index}
                >
                  {page.label}
                </option>
              ))}
            </StyledSelect>
          </StyledFormControl>

          <Typography className={classes.selectDivider}>to</Typography>

          <StyledFormControl
            error={error?.errorFocus === PAGE_SELECTION_ERRORS.END_PAGE}
            variant="outlined"
            size="small"
          >
            <StyledInputLabel htmlFor="end-page-select">
              {END_PAGE_LABEL}
            </StyledInputLabel>
            <StyledSelect
              disabled={!isNumber(pageRangeStart)}
              inputProps={{
                ...(Boolean(
                  error?.errorFocus === PAGE_SELECTION_ERRORS.END_PAGE,
                )
                  ? { 'aria-describedby': 'error-message' }
                  : ''),
                id: 'end-page-select',
                ref: endPageSelectRef,
                'aria-required': true,
              }}
              label={END_PAGE_LABEL}
              native
              onChange={(e) => {
                const value = !isNaN(e.target.value)
                  ? +e.target.value
                  : e.target.value;

                onSetPageRangeEnd(value);
              }}
              value={pageRangeEnd}
            >
              <option disabled value=""></option>
              {pages
                .filter((page) => page.index >= pageRangeStart)
                .map((page) => (
                  <option
                    className="noTranslate"
                    key={`end-page-${page.index}`}
                    value={page.index}
                  >
                    {page.label}
                  </option>
                ))}
            </StyledSelect>
          </StyledFormControl>
        </div>
        <Button
          aria-label="Add pages to Print Queue."
          className={classes.addButton}
          color="primary"
          disableFocusRipple
          id="add-to-print-queue-button"
          ref={addButtonRef}
          type="submit"
          variant="outlined"
          {...(displayAddButtonError
            ? { 'aria-describedby': 'error-message' }
            : '')}
        >
          Add
        </Button>
      </div>

      <ErrorMessage error={error} />
    </form>
  );
};

export default PageSelection;
